import { useEffect } from 'react';
import { isEnglishPath, localizedPath } from '../lib/localeRoutes';

const DEFAULT_DESCRIPTION = 'MiskiPOP: merch oficial con intención, estética y personalidad para fans y creadores.';

export function formatSeoTitle(title) {
  const normalized = String(title || '').trim().replace(/miskipop/gi, 'MiskiPOP');
  if (!normalized) return 'MiskiPOP';
  if (/^MiskiPOP\s*\|\s*MiskiPOP$/.test(normalized)) return 'MiskiPOP';
  return normalized.includes('MiskiPOP') ? normalized : `${normalized} | MiskiPOP`;
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    element.setAttribute(key, value);
  });
}

function upsertStructuredData(id, data) {
  let element = document.head.querySelector(`script[data-seo-jsonld="${id}"]`);
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function removeStructuredData(id) {
  document.head.querySelector(`script[data-seo-jsonld="${id}"]`)?.remove();
}

export default function Seo({
  title,
  description,
  canonical,
  image,
  type = 'website',
  robots,
  allowEnglishIndex = false,
  structuredData,
  structuredDataId = 'primary'
}) {
  useEffect(() => {
    const nextTitle = formatSeoTitle(title);
    const nextDescription = description || DEFAULT_DESCRIPTION;
    const english = isEnglishPath(window.location.pathname);
    const sourceUrl = new URL(canonical || window.location.href);
    const currentUrl = `${sourceUrl.origin}${localizedPath(sourceUrl.pathname, english ? 'en' : 'es')}`;
    const spanishUrl = `${sourceUrl.origin}${localizedPath(sourceUrl.pathname, 'es')}`;
    const englishUrl = `${sourceUrl.origin}${localizedPath(sourceUrl.pathname, 'en')}`;
    const isLegal = englishUrl === spanishUrl;
    const robotsValue = robots || (english && !allowEnglishIndex ? 'noindex,follow' : null);

    document.title = nextTitle;

    upsertMeta('meta[name="description"]', { name: 'description', content: nextDescription });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: nextTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: nextDescription });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: currentUrl });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'MiskiPOP' });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: nextTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: nextDescription });

    if (image) {
      upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
      upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
    } else {
      document.head.querySelector('meta[property="og:image"]')?.remove();
      document.head.querySelector('meta[name="twitter:image"]')?.remove();
    }

    if (robotsValue) upsertMeta('meta[name="robots"]', { name: 'robots', content: robotsValue });
    else document.head.querySelector('meta[name="robots"]')?.remove();

    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: currentUrl });
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
    if (allowEnglishIndex && !isLegal) {
      upsertLink('link[rel="alternate"][hreflang="es"]', { rel: 'alternate', hreflang: 'es', href: spanishUrl });
      upsertLink('link[rel="alternate"][hreflang="en"]', { rel: 'alternate', hreflang: 'en', href: englishUrl });
      upsertLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: spanishUrl });
    }

    if (structuredData) {
      upsertStructuredData(structuredDataId, structuredData);
    }

    return () => {
      if (structuredData) {
        removeStructuredData(structuredDataId);
      }
    };
  }, [title, description, canonical, image, type, robots, allowEnglishIndex, structuredData, structuredDataId]);

  return null;
}
